import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Stack, Tooltip, Typography, TextField } from "@mui/material";
import { Add, EditOutlined, Check, Close, StickyNote2Outlined } from "@mui/icons-material";
import type { Client, Job } from "../../interfaces/job.interfaces";
import { useJobEventsPaginated } from "../events/hooks/useJobEventsPaginated";
import { BaseModalComponent } from "../../../common/BaseModalComponent";
import { ScopeOfWork } from "../../../scope-of-work/types/scope-of-work.interface";
import { SelectClientStep } from "@components/jobs2/JobCreate/components/SelectClientStep";
import { ClientDetailsTabs } from "../../../clients/ClientDetailsModal/ClientDetailsTabs";
import { ClientNotes } from "../../../clients/Clients-notes/ClientNotes";
import { getRecordEmail as resolveClientEmail } from "../../../../utils";
import { useUpdateJobAliasMutation } from "@components/jobs/jobs-list/hooks/useUpdateJobAlias";
import { useUpdateServiceAlias } from "@components/jobs/hooks/useAupdateServiceName";

type Props = {
  job: Job;
  scopeOfWork?: ScopeOfWork | null;
  onJobUpdated?: () => void;
};

const ADD_CLIENT_LABEL = "No client selected";

export function JobDetailHeader({ job, scopeOfWork, onJobUpdated }: Props) {
  const clientProfileTriggerRef = useRef<HTMLButtonElement>(null);
  const { totalItems: visitsFromQuery, isSuccess: hasVisitsQueryLoaded } = useJobEventsPaginated({
    parentId: job?.id,
    pageSize: 1,
  });

  const updateJobAliasMutation = useUpdateJobAliasMutation();
  const { mutateAsync: updateServiceAlias, isPending: isUpdatingServiceAlias } = useUpdateServiceAlias();
  const [isEditingServiceAlias, setIsEditingServiceAlias] = useState(false);
  const [serviceAliasValue, setServiceAliasValue] = useState(scopeOfWork?.serviceNameOverride || "");
  const [isEditingAlias, setIsEditingAlias] = useState(false);
  const [aliasValue, setAliasValue] = useState(job?.alias || "");

  useEffect(() => {
    setAliasValue(job?.alias || "");
  }, [job?.alias]);

  useEffect(() => {
    setServiceAliasValue(scopeOfWork?.serviceNameOverride || "");
  }, [scopeOfWork?.serviceNameOverride]);

  const handleSaveAlias = async (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const cleanAlias = aliasValue.trim();

    if (cleanAlias === job?.alias?.trim()) {
      setIsEditingAlias(false);
      return;
    }

    try {
      await updateJobAliasMutation.mutateAsync({
        jobId: job.id,
        alias: cleanAlias || null,
      });

      onJobUpdated?.();
      setIsEditingAlias(false);
    } catch (error) {
      console.error("Error saving job alias:", error);
    }
  };

  const handleSaveServiceAlias = async (event?: React.MouseEvent | React.KeyboardEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    const cleanAlias = serviceAliasValue.trim();
    const currentAlias = scopeOfWork?.serviceNameOverride?.trim() || "";

    if (cleanAlias === currentAlias) {
      setIsEditingServiceAlias(false);
      return;
    }

    try {
      await updateServiceAlias({
        jobId: job.id,
        serviceNameOverride: cleanAlias || null,
      });

      setIsEditingServiceAlias(false);
      onJobUpdated?.();
    } catch (error) {
      console.error("Error saving service alias:", error);
    }
  };

  const handleCancelServiceAlias = (event?: React.MouseEvent | React.KeyboardEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    setServiceAliasValue(scopeOfWork?.serviceNameOverride || "");

    setIsEditingServiceAlias(false);
  };

  if (!job) return null;

  const handleCancelAlias = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setAliasValue(job?.alias || "");
    setIsEditingAlias(false);
  };

  const client = resolveJobClient(job);
  const clientUid = client?.uid ?? job.clientUid ?? null;
  const notes = typeof scopeOfWork?.meta?.notes === "string" ? scopeOfWork.meta.notes.trim() : "";
  const visitsCount = resolveVisitsCount(job, visitsFromQuery, hasVisitsQueryLoaded);
  const visitsLabel = visitsCount === 1 ? "1 visit" : `${visitsCount} visits`;

  const displayClientName = getClientDisplayName(job);
  const clientEmail = resolveClientEmail(client) || null;

  // --- ALIAS RULES ALIGNED WITH THE JOBCARD ---
  const alias = job?.alias?.trim() || "";
  const hasAlias = Boolean(alias);

  const serviceNameOverride = scopeOfWork?.serviceNameOverride?.trim() || null;

  const originalServiceName = scopeOfWork?.typeOfService?.name?.trim() || scopeOfWork?.name?.trim() || "Service";

  const jobDisplayId = job.id;
  const fallbackJobTitle = jobDisplayId ? `Job #${jobDisplayId}` : "Unnamed job";

  const primaryTitle = hasAlias ? alias : fallbackJobTitle;

  const normalizeJobLabel = (value?: string | number | null) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/^job#?/, "");

  const shouldShowJobIdPill =
    Boolean(jobDisplayId) &&
    hasAlias &&
    normalizeJobLabel(alias) !== normalizeJobLabel(jobDisplayId) &&
    normalizeJobLabel(alias) !== normalizeJobLabel(fallbackJobTitle);

  const hasClient = displayClientName !== ADD_CLIENT_LABEL;
  const canOpenClientProfile = hasClient && Boolean(clientUid);

  const openClientProfile = () => {
    if (!canOpenClientProfile) return;
    clientProfileTriggerRef.current?.click();
  };

  const clientLinkSx = {
    color: "text.secondary",
    fontSize: "inherit",
    lineHeight: "inherit",
    fontWeight: "inherit",
    border: "none",
    background: "none",
    padding: 0,
    cursor: canOpenClientProfile ? "pointer" : "default",
    textDecoration: "none",
    whiteSpace: "nowrap" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    "&:hover": canOpenClientProfile
      ? {
          color: "primary.main",
          textDecoration: "underline",
        }
      : undefined,
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
        borderRadius: 3,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ width: "100%", minWidth: 0 }}>
          <AvatarSoft name={displayClientName} />

          <Box sx={{ minWidth: 0, width: "100%" }}>
            {/* --- PRIMARY TITLE / ALIAS EDIT MODE --- */}
            {isEditingAlias ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <TextField
                  value={aliasValue}
                  onChange={(e) => setAliasValue(e.target.value)}
                  placeholder="Enter alias..."
                  size="small"
                  variant="standard"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveAlias(e);
                    if (e.key === "Escape") handleCancelAlias(e);
                  }}
                  sx={{
                    input: {
                      fontSize: "24px",
                      fontWeight: 600,
                      lineHeight: 1.15,
                      padding: "2px 0px",
                    },
                  }}
                />
                <Tooltip title="Save Alias" arrow>
                  <IconButton
                    size="small"
                    disabled={updateJobAliasMutation.isPending}
                    onClick={(e) => handleSaveAlias(e)}
                    sx={{ color: "success.main" }}
                  >
                    <Check sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel" arrow>
                  <IconButton size="small" onClick={(e) => handleCancelAlias(e)} sx={{ color: "text.disabled" }}>
                    <Close sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 24,
                    lineHeight: 1.15,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {primaryTitle}
                </Typography>

                {hasAlias && (
                  <Tooltip title="Edit alias" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAliasValue(job?.alias || "");
                        setIsEditingAlias(true);
                      }}
                      sx={{
                        color: "primary.main",
                        "&:hover": {
                          color: "primary.dark",
                          bgcolor: "rgba(25, 118, 210, 0.08)",
                        },
                      }}
                    >
                      <EditOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
            {!hasAlias && !isEditingAlias && (
              <Tooltip title="Add a custom name for this job" arrow>
                <Typography
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setAliasValue("");
                    setIsEditingAlias(true);
                  }}
                  sx={{
                    mt: 0.5,
                    display: "inline-flex",
                    width: "fit-content",
                    border: "none",
                    background: "none",
                    p: 0,
                    color: "primary.main",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  + Add alias
                </Typography>
              </Tooltip>
            )}

            {/* --- SERVICE INFO ROW --- */}
            {!isEditingAlias && (
              <Box
                sx={{
                  mt: 0.5,
                  minWidth: 0,
                }}
              >
                {isEditingServiceAlias ? (
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      Service alias:
                    </Typography>

                    <TextField
                      value={serviceAliasValue}
                      onChange={(event) => setServiceAliasValue(event.target.value)}
                      placeholder={originalServiceName}
                      size="small"
                      variant="standard"
                      autoFocus
                      disabled={isUpdatingServiceAlias}
                      inputProps={{
                        maxLength: 150,
                      }}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          handleSaveServiceAlias(event);
                        }

                        if (event.key === "Escape") {
                          handleCancelServiceAlias(event);
                        }
                      }}
                      sx={{
                        minWidth: 160,
                        maxWidth: 360,
                        flex: 1,

                        "& input": {
                          fontSize: "0.875rem",
                          lineHeight: 1.43,
                          py: 0.25,
                        },
                      }}
                    />

                    <Tooltip title="Save service alias" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isUpdatingServiceAlias}
                          onClick={handleSaveServiceAlias}
                          sx={{
                            width: 26,
                            height: 26,
                            color: "success.main",
                          }}
                        >
                          <Check sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Cancel" arrow>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isUpdatingServiceAlias}
                          onClick={handleCancelServiceAlias}
                          sx={{
                            width: 26,
                            height: 26,
                            color: "text.disabled",
                          }}
                        >
                          <Close sx={{ fontSize: 18 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                      minWidth: 0,
                      color: "text.secondary",
                      fontSize: "0.875rem",
                      lineHeight: 1.43,
                    }}
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {serviceNameOverride ? "Service alias:" : "Service:"}
                    </Typography>

                    <Typography
                      component="span"
                      variant="body2"
                      title={serviceNameOverride || originalServiceName}
                      sx={{
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {serviceNameOverride || originalServiceName}
                    </Typography>

                    <Tooltip title={serviceNameOverride ? "Edit service alias" : "Add service alias"} arrow>
                      <IconButton
                        size="small"
                        disabled={!scopeOfWork}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();

                          setServiceAliasValue(scopeOfWork?.serviceNameOverride || "");

                          setIsEditingServiceAlias(true);
                        }}
                        sx={{
                          width: 26,
                          height: 26,
                          flexShrink: 0,
                          color: "primary.main",

                          "&:hover": {
                            color: "primary.dark",
                            bgcolor: "rgba(25, 118, 210, 0.08)",
                          },
                        }}
                      >
                        {serviceNameOverride ? <EditOutlined sx={{ fontSize: 16 }} /> : <Add sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>

                    {serviceNameOverride && (
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            flexShrink: 0,
                          }}
                        >
                          ·
                        </Typography>

                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: "text.secondary",
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          Original service:
                        </Typography>

                        <Typography
                          component="span"
                          variant="body2"
                          title={originalServiceName}
                          sx={{
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {originalServiceName}
                        </Typography>
                      </>
                    )}
                  </Stack>
                )}
              </Box>
            )}

            {/* --- CLIENT INFO ROW --- */}
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5, minWidth: 0 }}>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  minWidth: 0,
                  gap: 0.5,
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  lineHeight: 1.43,
                }}
              >
                <Typography component="span" variant="body2" sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0 }}>
                  Client:
                </Typography>
                <Box component="button" type="button" onClick={openClientProfile} disabled={!canOpenClientProfile} sx={clientLinkSx}>
                  {displayClientName}
                </Box>

                {clientEmail ? (
                  <>
                    <Typography component="span" variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                      ·
                    </Typography>
                    <Box component="button" type="button" onClick={openClientProfile} disabled={!canOpenClientProfile} sx={clientLinkSx}>
                      {clientEmail}
                    </Box>
                  </>
                ) : null}
              </Box>

              <BaseModalComponent
                modalKey={`Job-Add-Client-${job.id}`}
                modalLabel={hasClient ? "Edit Client" : "Add Client"}
                elementTrigger={
                  <Tooltip title={hasClient ? "Edit client" : "Add client"}>
                    <IconButton
                      size="small"
                      aria-label={hasClient ? "Edit client" : "Add client"}
                      sx={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        color: "primary.main",
                        "&:hover": {
                          bgcolor: "rgba(25, 118, 210, 0.08)",
                        },
                      }}
                    >
                      {hasClient ? <EditOutlined sx={{ fontSize: 16 }} /> : <Add sx={{ fontSize: 16 }} />}
                    </IconButton>
                  </Tooltip>
                }
              >
                <SelectClientStep isUpdateJob={true} jobId={job.id} onUpdated={onJobUpdated} />
              </BaseModalComponent>

              {canOpenClientProfile && (
                <BaseModalComponent
                  modalKey={`Job-Client-Notes-${job.id}`}
                  modalLabel={`${displayClientName} - Notes`}
                  defaultSizes={{ width: { xs: "95%", sm: "680px", md: "780px" }, height: { xs: "90%", sm: "640px", md: "700px" } }}
                  allowInsidePreventModal
                  elementTrigger={
                    <Tooltip title="View Client Notes">
                      <IconButton
                        size="small"
                        aria-label="View Client Notes"
                        sx={{
                          flexShrink: 0,
                          width: 28,
                          height: 28,
                          color: "#ff8222",
                          "&:hover": {
                            bgcolor: "rgba(255, 130, 34, 0.08)",
                          },
                        }}
                      >
                        <StickyNote2Outlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Box sx={{ p: 2, height: "100%", overflowY: "auto" }}>
                    <ClientNotes clientUid={clientUid} />
                  </Box>
                </BaseModalComponent>
              )}

              {canOpenClientProfile ? (
                <BaseModalComponent
                  modalKey={`Job-Client-Profile-${job.id}`}
                  modalLabel={displayClientName}
                  defaultSizes={{ width: 700 }}
                  allowInsidePreventModal
                  elementTrigger={
                    <button
                      ref={clientProfileTriggerRef}
                      type="button"
                      style={{ position: "absolute", left: -99999, top: -99999, visibility: "hidden" }}
                      aria-hidden
                    />
                  }
                >
                  <ClientDetailsTabs client={client} clientUid={clientUid} onClientUpdated={onJobUpdated} />
                </BaseModalComponent>
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
        {shouldShowJobIdPill && <Pill label={`Job #${jobDisplayId}`} tone="info" />}
        <Pill label={formatCreated(job.createdAt)} />
        <Pill label={visitsLabel} />
      </Stack>

      {notes && (
        <Box
          sx={{
            mt: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: "background.default",
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              color: "text.secondary",
              mb: 0.5,
              letterSpacing: 0.2,
              textTransform: "uppercase",
            }}
          >
            Notes
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              color: "text.primary",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {notes}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function AvatarSoft({ name }: { name: string }) {
  const initial = name.trim()[0]?.toUpperCase() ?? "C";

  return (
    <Box
      sx={{
        width: 70,
        height: 70,
        borderRadius: "999px",
        bgcolor: "action.hover",
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "text.primary" }}>{initial}</Typography>
    </Box>
  );
}

type PillTone = "default" | "info";

function Pill({ label, tone = "default" }: { label: string; tone?: PillTone }) {
  const tones: Record<PillTone, { bg: string; fg: string; ring: string }> = {
    default: {
      bg: "background.default",
      fg: "text.secondary",
      ring: "rgba(0,0,0,0.06)",
    },
    info: {
      bg: "rgba(25, 118, 210, 0.06)",
      fg: "rgba(25, 118, 210, 0.9)",
      ring: "rgba(25, 118, 210, 0.10)",
    },
  };

  const selectedTone = tones[tone];

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.6,
        borderRadius: "999px",
        bgcolor: selectedTone.bg,
        color: selectedTone.fg,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0.2,
        boxShadow: `inset 0 0 0 1px ${selectedTone.ring}`,
        maxWidth: "100%",
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: "inherit",
          fontWeight: "inherit",
          color: "inherit",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function resolveVisitsCount(job: Job, visitsFromQuery: number, hasVisitsQueryLoaded: boolean) {
  if (job.eventsCount != null && !Number.isNaN(Number(job.eventsCount))) {
    return Number(job.eventsCount);
  }

  if (hasVisitsQueryLoaded) {
    return visitsFromQuery;
  }

  return job.events?.length ?? 0;
}

function resolveJobClient(job: Job): Client | null {
  const quoteClient = (job as Job & { quote?: { client?: Client | null } }).quote?.client;

  return job.client ?? quoteClient ?? job.events?.[0]?.client ?? job.events?.[0]?.job?.client ?? null;
}

function getClientDisplayName(job: Job) {
  const client = resolveJobClient(job);
  const name = buildFullName(client?.firstName, client?.lastName);

  return name || ADD_CLIENT_LABEL;
}

function buildFullName(firstName?: string | null, lastName?: string | null) {
  return [firstName, lastName].filter(Boolean).join(" ").trim();
}

function formatCreated(iso?: string) {
  if (!iso) return "Created —";

  const date = new Date(iso);
  const month = date.toLocaleString(undefined, { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();

  return `Created ${month} ${day}, ${year}`;
}

function normalizeLabel(value?: string | null) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}
